import { SelectField } from '@flos/react-ui';
import type { OptionProps } from '@flos/react-ui/build/core/forms/select/select-input';
import type { OPCTermDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc';
import { useHippoText } from '../../../../hooks/useHippoText';
import { formatStringToMonetaryValueString } from '../../../../utils/coverablesUtil';
import { PRH_FIRE_DEDUCTIBLE, PRH_STORM_DEDUCTIBLE } from '../../../../constants/term';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { isOpcFetchingAtom } from '../../../../recoil/atoms/isOpcFetchingAtom';
import { callMeVariantOpcAtom } from '../../../../recoil/atoms/callMeVariantOpcAtom';
import { useValidateTermChangeOPC } from '../../../../hooks/useValidateTermChangeOPC';

const getInfoMsg = (termId?: string): string | undefined => {
    switch (termId) {
        case PRH_FIRE_DEDUCTIBLE:
            return 'DSS-PRH-EDIT-FIRE-DEDUCTIBLE-INFO';
        case PRH_STORM_DEDUCTIBLE:
            return 'DSS-PRH-EDIT-STORM-DEDUCTIBLE-INFO';
        default:
            return undefined;
    }
};

const AdjustDeductible = ({
    termInfo,
    updateTermValue,
    isAdjustableDataValid,
    overrideSubHeader,
    overrideErrorMsg
}: {
    termInfo: OPCTermDTO;
    updateTermValue: (termValue: number, publicId?: string) => void;
    isAdjustableDataValid: boolean;
    overrideSubHeader?: string;
    overrideErrorMsg?: string;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const setCallMeVariant = useSetRecoilState(callMeVariantOpcAtom);
    const isOpcFetching = useRecoilValue(isOpcFetchingAtom);
    const { isNewTermValidatedSuccessfully } = useValidateTermChangeOPC();
    const options: OptionProps[] = termInfo.options?.map((option) => ({
        label: formatStringToMonetaryValueString(String(option.code)),
        value: option.code
    })) as OptionProps[];

    const handleTermChanged = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedIndex = event.target.selectedIndex;
        const { value } = event.target.options[selectedIndex];
        void isNewTermValidatedSuccessfully(Number(value)).then((result) => {
            if (result) {
                updateTermValue(Number(value), termInfo?.publicId);
            } else {
                setCallMeVariant('opcTermInvalidValue');
            }
        });
    };

    const errorMsg = overrideErrorMsg
        ? getHippoText(overrideErrorMsg)
        : 'Du har valgt den samme selvrisiko, som du allerede har. Vælg en anden, eller klik fortryd.';

    const infoMsg = getInfoMsg(termInfo.publicId);

    return (
        <>
            <h6>
                {getHippoText(overrideSubHeader ?? 'DSS-COVERABLE-DETAILS-ADAPT-DEDUCTIBLE-INPUT')}
            </h6>
            <SelectField
                label={getHippoText('DSS-OPC-INPUT-LABEL')}
                className="u-spacing-stack-none"
                options={options}
                placeholder={formatStringToMonetaryValueString(String(termInfo.value))}
                onChange={handleTermChanged}
                isValid={isAdjustableDataValid}
                errorText=""
                disabled={isOpcFetching}
            />
            {!isAdjustableDataValid && (
                <p className="paragraph--small u-color-supplement-error-base">{errorMsg}</p>
            )}
            {infoMsg && (
                <p className="paragraph--small u-color-supplement-grey-base">
                    {getHippoText(infoMsg)}
                </p>
            )}
        </>
    );
};

export default AdjustDeductible;
